import { ScenarioApi } from '../../ScenarioApi';
import type { Api } from '@automapa/svelte-automapa-template';
import type { GetTargetStateResponseType } from '../../TargetStage';

export type IdMapType = {
    busy: boolean;
    todo: string;
};

export type FunctionOnChangeIdsType = Map<number, { lastStatus: boolean }>;

export class StageTargetProcessingChecker {
    private scenarioId: number;
    private ids: Map<number, IdMapType> = new Map(); // true - busy/in progress, false - done
    private timeoutHandler: any | null = null;
    private readonly api: Api;
    private intervalSeconds = 5;
    private functionOnChange: (ids: FunctionOnChangeIdsType) => void;

    constructor(
        api: Api,
        scenarioId: number,
        functionOnChange: (ids: FunctionOnChangeIdsType) => void
    ) {
        // console.log('StageTargetProcessingChecker, constructor', scenarioId);
        this.api = api;
        this.scenarioId = scenarioId;
        this.functionOnChange = functionOnChange;
    }

    getAssignmentIds(): { [key: number]: IdMapType } {
        return Object.fromEntries(this.ids.entries());
    }

    getAssignmentIdsBusyOnly(): { [key: number]: IdMapType } {
        return Object.fromEntries(
            Array.from(this.ids.entries()).filter(([key, value]) => value.busy)
        );
    }

    addAssignment(id: number) {
        this.ids.set(id, { busy: true, todo: 'unknown' });
        // console.log('StageTargetProcessingChecker, addAssignment', id);
        if (this.timeoutHandler === null) {
            // console.log('StageTargetProcessingChecker, autostart');
            this.start();
        }
    }

    removeAssignment(id: number) {
        this.ids.delete(id);
        // console.log('StageTargetProcessingChecker, removeAssignment', id);
    }

    async runIntervalFunction(): Promise<boolean> {
        let response: GetTargetStateResponseType | undefined = await ScenarioApi.getTargetStage(
            this.api,
            this.scenarioId
        ).catch(e => {
            console.error('StageTargetProcessingChecker, runIntervalFunction', e);
            this.stop(); // maybe scenarioId is changed - so stop checking - https://er.automapa.pl/issues/46263
            return undefined;
        });
        if (response) {
            // console.log('StageTargetProcessingChecker, runIntervalFunction', response.todo);
            let { newIds, diffInfo } = StageTargetProcessingChecker._extractNewIdsAndDiffInfo(
                response,
                this.ids
            );

            this.ids = newIds;

            // call functionOnChange with changed ids only
            this.functionOnChange(diffInfo);
            return true; // response received, continue checking
        } else {
            return false; // no response, stop checking
        }
    }

    static _extractNewIdsAndDiffInfo(
        response: GetTargetStateResponseType,
        currentIds: Map<number, IdMapType>
    ): {
        newIds: Map<number, IdMapType>;
        diffInfo: Map<number, { lastStatus: boolean }>;
    } {
        // console.log('StageTargetProcessingChecker, _extractNewIdsAndDiffInfo', response);
        // console.log('StageTargetProcessingChecker, _extractNewIdsAndDiffInfo', currentIds);

        let newIds = new Map<number, IdMapType>(currentIds);
        let todoKeys = Object.keys(response.todo);

        // const allValuesFromTodoResponse: number[] = Object.values(response.todo).flat();
        const allValuesFromResponse: number[] = Object.values(response).flatMap(stage =>
            Object.values(stage).flat()
        );

        // set this.ids busy status to false by default
        newIds.forEach((value, key) => {
            newIds.set(key, { busy: false, todo: value.todo });
        });

        todoKeys.forEach(key => {
            if (typeof response.todo[key] !== 'undefined') {
                response.todo[key].forEach((id: number) => {
                    newIds.set(id, { busy: true, todo: key });
                });
            }
        });

        // update diffIds with ids where todo has changed
        let diffInfo = new Map<number, { lastStatus: boolean }>();
        newIds.forEach((value, key) => {
            // console.warn('newIds', key, value);
            if (currentIds.has(key) && currentIds.get(key)?.todo !== value.todo) {
                // diffIds.set(key, { lastStatus: this.ids.get(key)?.busy || false });
                diffInfo.set(key, { lastStatus: false });
            }
        });

        // not busy ids remove (add diffIds with lastStatus = true),
        newIds.forEach((value, key) => {
            if (!value.busy) {
                diffInfo.set(key, { lastStatus: true });
            }
        });

        // restore ids which exists in stage_current - hack to not remove them when not visible for while in statuses todo
        allValuesFromResponse.forEach((id: number) => {
            diffInfo.set(id, { lastStatus: false });
        });

        // remove ids which are not busy anymore and not in stage_current
        diffInfo.forEach((value, key) => {
            if (value.lastStatus) {
                newIds.delete(key);
            }
        });

        // console.log('StageTargetProcessingChecker, _extractNewIdsAndDiffInfo, newIds', newIds);
        // console.log('StageTargetProcessingChecker, _extractNewIdsAndDiffInfo, diffInfo', diffInfo);

        return { newIds, diffInfo };
    }

    start() {
        this.stop();
        this.timeoutHandler = setTimeout(async () => {
            let success = await this.runIntervalFunction();
            if (!success) {
                console.warn('StageTargetProcessingChecker, no response from API, stopping');
                this.stop(); // stop checking if no response
            } else {
                this.start();
            }
        }, this.intervalSeconds * 1000);
    }

    stop() {
        if (this.timeoutHandler !== null) {
            clearTimeout(this.timeoutHandler);
        } else {
            console.warn('StageTargetProcessingChecker, stop called when not running');
        }
    }
}
